"use client";

import { useState } from "react";
import { Spinner } from "@tillix/ui-atoms/atoms/spinner";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import { Form } from "@tillix/ui-atoms/form";
import { Separator } from "@tillix/ui-atoms/separator";
import { AnimatePresence, motion } from "motion/react";
import { FormProvider, useForm } from "react-hook-form";
import { clearUserMissing } from "./actions";
import AchSection from "./sections/AchSection";
import AddressSection from "./sections/AddressSection";
import DocsSection from "./sections/DocsSection";
import SsnSection from "./sections/SsnSection";
import type { NigoFlags, NigoFormValues } from "./types";

interface NigoFormProps {
  flags: NigoFlags;
  userId: string;
  onSubmitted?: () => void;
}

export default function NigoForm({
  flags,
  userId,
  onSubmitted,
}: NigoFormProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<NigoFormValues>({
    defaultValues: {
      ssn: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      achFirstName: "",
      achLastName: "",
      routingNumber: "",
      accountNumber: "",
      accountType: "",
    },
    mode: "onChange",
  });

  const hasAnyFlag =
    flags.missingSSN ||
    flags.missingAddress ||
    flags.missingACH ||
    flags.missingDocs;

  const onSubmit = async (values: NigoFormValues) => {
    const payload = { ...values, files };
    console.log("NIGO form submitted:", payload);
    await clearUserMissing(userId);
    await new Promise((resolve) => setTimeout(resolve, 700));
    setSubmitted(true);
    onSubmitted?.();
  };

  const showDone = !hasAnyFlag || submitted;

  const sections: React.ReactNode[] = [];

  if (flags.missingSSN) sections.push(<SsnSection key="ssn" />);
  if (flags.missingAddress) sections.push(<AddressSection key="address" />);
  if (flags.missingACH) sections.push(<AchSection key="ach" />);
  if (flags.missingDocs)
    sections.push(
      <DocsSection files={files} key="docs" onFilesChange={setFiles} />,
    );

  return (
    <AnimatePresence mode="wait">
      {showDone ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 8 }}
          key="done"
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <Card>
            <Card.Content className="py-8 text-center text-muted-foreground">
              You have no pending work items — you're all set!
            </Card.Content>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          exit={{ opacity: 0, y: -8 }}
          key="form"
          transition={{ duration: 0.25, ease: "easeIn" }}
        >
          <FormProvider {...form}>
            <Form onSubmit={form.handleSubmit(onSubmit)}>
              <Card>
                <Card.Content className="flex flex-col gap-6 rounded pt-6">
                  {sections.map((section, i) => (
                    <div key={(section as React.ReactElement).key}>
                      {i > 0 && <Separator className="mb-6" />}
                      {section}
                    </div>
                  ))}
                </Card.Content>
                <Card.Footer>
                  <Button disabled={form.formState.isSubmitting} type="submit">
                    {form.formState.isSubmitting ? (
                      <>
                        <Spinner className="mr-2" />
                        Submitting…
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            </Form>
          </FormProvider>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
